import { z } from 'zod';
import type { Channel } from '.';

/**
 * One (order × requested document type) unit of a bulk-print batch. Every unit
 * the batch was asked to produce appears exactly once — generated, skipped
 * (ineligible, with a machine-readable reason), or failed (render error).
 * Skips/failures are surfaced as "needs attention", never silently dropped.
 */
const BulkPrintUnitResultSchema = z
  .object({
    orderId: z.number(),
    orderSlug: z.string(),
    orderKind: z.enum(['SALES_ORDER', 'BUY_SELL_ORDER']),
    /** The requested menu row (entityType, documentType) this unit belongs to. */
    entityType: z.string(),
    documentType: z.string(),
    status: z.enum(['generated', 'skipped', 'failed']),
    /** Machine-readable reason code. Required whenever status is not `generated`. */
    reason: z.string().optional(),
    /** Human-readable elaboration shown to the operator. */
    detail: z.string().optional(),
  })
  .superRefine((unit, ctx) => {
    if (unit.status !== 'generated' && !unit.reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reason'],
        message: `a ${unit.status} unit must carry a reason code`,
      });
    }
  });

/** One merged, uploaded PDF. `delivery: combined` → one artifact with no
 * documentType; `delivery: separate` → one artifact per requested menu row. */
const BulkPrintArtifactSchema = z.object({
  title: z.string(),
  url: z.string().url(),
  entityType: z.string().optional(),
  documentType: z.string().optional(),
});

/** One merge/upload group — the print-job row the client renders a status pill for. */
const BulkPrintGroupSchema = z.object({
  key: z.string(),
  /** Absent for delivery: combined, where the single group spans every type. */
  entityType: z.string().optional(),
  documentType: z.string().optional(),
  /** Renderable units in the group — the row's honest "N orders" count. */
  unitCount: z.number(),
});

export const BulkPrintChannelSchema = {
  /**
   * Published once the batch knows its real shape: the groups it will render
   * (each a client print-job row, initially Queued) and the units already ruled
   * out during resolution. Lets the modal show true counts and needs-attention
   * entries long before any PDF exists.
   */
  batchStarted: z.object({
    batchId: z.string(),
    groups: z.array(BulkPrintGroupSchema),
    needsAttention: z.array(BulkPrintUnitResultSchema),
  }),
  /**
   * Published on both edges of every group's render step, so a print-job row's
   * pill moves Queued → Building… → Generated on real backend transitions
   * rather than a client-side timer.
   */
  groupProgress: z.object({
    batchId: z.string(),
    key: z.string(),
    status: z.enum(['building', 'generated', 'failed']),
    generatedCount: z.number().optional(),
    failedCount: z.number().optional(),
  }),
  /** Terminal message: merged artifact URL(s) plus the per-unit outcome list. */
  batchCompleted: z.object({
    batchId: z.string(),
    status: z.enum(['completed', 'completed_with_issues']),
    artifacts: z.array(BulkPrintArtifactSchema),
    results: z.array(BulkPrintUnitResultSchema),
  }),
} as const;

export type BulkPrintUnitResultMessage = z.infer<typeof BulkPrintUnitResultSchema>;
export type BulkPrintArtifactMessage = z.infer<typeof BulkPrintArtifactSchema>;
export type BulkPrintGroupMessage = z.infer<typeof BulkPrintGroupSchema>;
export type BulkPrintBatchStarted = z.infer<(typeof BulkPrintChannelSchema)['batchStarted']>;
export type BulkPrintGroupProgress = z.infer<(typeof BulkPrintChannelSchema)['groupProgress']>;
export type BulkPrintBatchCompleted = z.infer<(typeof BulkPrintChannelSchema)['batchCompleted']>;

export interface BulkPrintChannelParams {
  organizationId: number;
  batchId: string;
}

/**
 * The org-scoped identity of one batch. The realtime channel id and the job's idempotency key are
 * both this composite — minted here so the two cannot drift apart.
 */
export const bulkPrintBatchKey = ({ organizationId, batchId }: BulkPrintChannelParams): string =>
  `${organizationId}:${batchId}`;

/**
 * Realtime channel for one bulk-print batch. The batch id is minted by the
 * client so it can subscribe BEFORE triggering the batch (Inngest realtime has
 * no replay); the org segment is always the server's own org for the acting
 * user, so a guessed batch id still cannot cross an org boundary.
 */
export const BulkPrintChannel: Channel<BulkPrintChannelParams, typeof BulkPrintChannelSchema> = {
  getChannelId: (params) => `bulkPrint:${bulkPrintBatchKey(params)}`,
  topics: { batchStarted: 'batchStarted', groupProgress: 'groupProgress', batchCompleted: 'batchCompleted' },
  schemas: BulkPrintChannelSchema,
};
